import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  PLATFORM_ID,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '@vaki/shared/util/environment';
import { GoogleTagManagerService } from './google-tag-manager';
import { SmartContractComponent } from '@vaki/shared/web3/feature/smart-contract';

// 'es' Spanish is our default language
registerLocaleData(localeEs, 'es');

@Component({
  selector: 'vaki-root',
  // `@defer (on idle)` keeps React/ReactDOM/Privy (statically imported by
  // SmartContractComponent for the Web3 cross-app flow) out of the initial
  // bundle. It only ever mounts once a `CrossAppCommunicationService`
  // action arrives — never on page load for a non-Web3 vaki — so deferring
  // its *download* changes nothing at runtime for the 99%+ of donors who
  // never trigger it. Measured -715 KB (-44%) off the initial bundle; see
  // the perf issue's feasibility spike. Risk flagged there and left for a
  // human/QA call, not resolved by this change: whether `on idle` is early
  // enough for a Web3 login/cross-app redirect callback that fires before
  // the browser goes idle — out of scope for this bundle-only slice.
  template: `
    @defer (on idle) {
      <lib-smart-contract></lib-smart-contract>
    }
    <router-outlet></router-outlet>
  `,
  imports: [RouterModule, SmartContractComponent]
})
export class AppComponent implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  private readonly gtmService = inject(GoogleTagManagerService, {
    optional: true
  });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // eslint-disable-next-line no-console
      console.log('🐮📱', environment.appVersion);
    }
  }

  ngAfterViewInit(): void {
    if (this.gtmService && isPlatformBrowser(this.platformId)) {
      this.startLoggingGtagManager();
    }
  }

  startLoggingGtagManager(): void {
    if (this.gtmService) {
      // Uses takeUntilDestroyed for proper cleanup (bfcache friendly)
      this.router.events
        .pipe(
          filter(
            (event): event is NavigationEnd => event instanceof NavigationEnd
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((event) => {
          this.gtmService?.pushTag({ event: 'page', pageName: event.url });
        });
    }
  }
}
